import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

const HOUR_VALUES = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_VALUES = [0, 15, 30, 45];
const POPOVER_WIDTH_HOUR = 252;
const POPOVER_WIDTH_MINUTE = 200;
const POPOVER_GAP = 8;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function TimeGrid({ title, values, selected, bubbleId, onSelect, compact }) {
  return (
    <motion.div
      key={title}
      className="time-picker-step"
      initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={{ opacity: 0, y: -6, filter: "blur(3px)" }}
      transition={{ type: "spring", duration: 0.28, bounce: 0.1 }}
    >
      <div className="time-picker-step-title">{title}</div>
      <div
        className={`time-picker-grid${
          values.length <= 4 ? " time-picker-grid--minutes" : ""
        }${compact ? " time-picker-grid--compact" : ""}`}
      >
        {values.map((value) => {
          const isSelected = selected === value;
          return (
            <button
              key={value}
              type="button"
              className={`time-picker-cell${isSelected ? " is-selected" : ""}`}
              onClick={() => onSelect(value)}
            >
              {isSelected ? (
                <motion.span
                  layoutId={bubbleId}
                  className="time-picker-bubble"
                  transition={{ type: "spring", duration: 0.38, bounce: 0.18 }}
                />
              ) : null}
              <span className="time-picker-cell-label">{pad2(value)}</span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

export default function ScheduleTimePicker({
  pickerId = "default",
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  disabled = false,
}) {
  const hourTriggerRef = useRef(null);
  const minuteTriggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("hour");
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, width: POPOVER_WIDTH_HOUR });

  const updatePopoverPos = useCallback(() => {
    const isMinute = step === "minute";
    const el = isMinute ? minuteTriggerRef.current : hourTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const minWidth = isMinute ? POPOVER_WIDTH_MINUTE : POPOVER_WIDTH_HOUR;
    const width = Math.max(minWidth, Math.round(rect.width));
    let left = rect.left;
    const maxLeft = window.innerWidth - width - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    setPopoverPos({
      top: rect.top - POPOVER_GAP,
      left,
      width,
    });
  }, [step]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopoverPos();
    const raf = window.requestAnimationFrame(updatePopoverPos);
    window.addEventListener("resize", updatePopoverPos);
    window.addEventListener("scroll", updatePopoverPos, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePopoverPos);
      window.removeEventListener("scroll", updatePopoverPos, true);
    };
  }, [open, step, updatePopoverPos]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === "Escape") closePicker();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  function openPicker(startStep = "hour") {
    if (disabled) return;
    setStep(startStep);
    setOpen(true);
    window.requestAnimationFrame(updatePopoverPos);
  }

  function closePicker() {
    setOpen(false);
    setStep("hour");
  }

  function handleHourSelect(value) {
    onHourChange(value);
    window.setTimeout(() => setStep("minute"), 180);
  }

  function handleMinuteSelect(value) {
    onMinuteChange(value);
    window.setTimeout(() => closePicker(), 140);
  }

  const popover =
    typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            {open ? (
              <>
                <motion.button
                  type="button"
                  className="time-picker-dismiss"
                  aria-label="Kapat"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={closePicker}
                />
                <div
                  className="time-picker-popover-shell"
                  style={{
                    top: popoverPos.top,
                    left: popoverPos.left,
                    width: popoverPos.width,
                  }}
                >
                  <motion.div
                    className="time-picker-popover"
                    role="dialog"
                    aria-modal="true"
                    aria-label={step === "hour" ? "Saat sec" : "Dakika sec"}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ type: "spring", duration: 0.3, bounce: 0.12 }}
                  >
                    <AnimatePresence mode="wait">
                      {step === "hour" ? (
                        <TimeGrid
                          title="Saat Seç"
                          values={HOUR_VALUES}
                          selected={hour}
                          bubbleId={`schedule-hour-bubble-${pickerId}`}
                          onSelect={handleHourSelect}
                          compact
                        />
                      ) : (
                        <TimeGrid
                          title="Dakika Seç"
                          values={MINUTE_VALUES}
                          selected={minute}
                          bubbleId={`schedule-minute-bubble-${pickerId}`}
                          onSelect={handleMinuteSelect}
                          compact
                        />
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </>
            ) : null}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      <div className="schedule-time-picker">
        <div className="auto-schedule-field time-picker-anchor">
          <label className="label">Saat</label>
          <button
            ref={hourTriggerRef}
            type="button"
            className={`time-picker-trigger input${open && step === "hour" ? " is-open" : ""}`}
            disabled={disabled}
            onClick={() => openPicker("hour")}
          >
            {pad2(hour)}
          </button>
        </div>
        <div className="auto-schedule-field">
          <label className="label">Dakika</label>
          <button
            ref={minuteTriggerRef}
            type="button"
            className={`time-picker-trigger input${open && step === "minute" ? " is-open" : ""}`}
            disabled={disabled}
            onClick={() => openPicker("minute")}
          >
            {pad2(minute)}
          </button>
        </div>
      </div>
      {popover}
    </>
  );
}
